import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, Download, Loader2, AlertCircle, RefreshCw, Settings } from "lucide-react";
import { api, type VendorEvaluation } from "@/services/api";

type ImageGenerationProvider = 'gemini' | 'dalle';

interface InfographicResponse {
  success: boolean;
  image_url?: string;
  image_base64?: string;
  provider: ImageGenerationProvider;
  error?: string;
  cached?: boolean;
}

interface ProvidersResponse {
  success: boolean;
  providers: ImageGenerationProvider[];
  default: ImageGenerationProvider | null;
}

/**
 * Infographic Report Page - AI-generated visual summary of vendor evaluation.
 */
export default function InfographicReportPage() {
  const navigate = useNavigate();
  const { evaluationId } = useParams<{ evaluationId: string }>();
  
  const [evaluation, setEvaluation] = useState<VendorEvaluation | null>(null);
  const [infographic, setInfographic] = useState<InfographicResponse | null>(null);
  const [availableProviders, setAvailableProviders] = useState<ImageGenerationProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<ImageGenerationProvider>('gemini');
  const [isLoadingEvaluation, setIsLoadingEvaluation] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProviderSettings, setShowProviderSettings] = useState(false);

  // Fetch evaluation and available providers on mount
  useEffect(() => {
    if (!evaluationId) {
      setError("No evaluation ID provided");
      setIsLoadingEvaluation(false);
      return;
    }

    async function fetchData() {
      if (!evaluationId) return;
      
      try {
        setIsLoadingEvaluation(true);
        
        // Fetch evaluation and providers in parallel
        const [evalResponse, providersResponse] = await Promise.all([
          api.getEvaluation(evaluationId),
          fetchProviders(),
        ]);
        
        setEvaluation(evalResponse.evaluation);
        
        if (providersResponse.providers.length > 0) {
          setAvailableProviders(providersResponse.providers);
          setSelectedProvider(providersResponse.default || providersResponse.providers[0]);
        }
        
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(err instanceof Error ? err.message : "Failed to load evaluation");
      } finally {
        setIsLoadingEvaluation(false);
      }
    }

    fetchData();
  }, [evaluationId]);

  // Auto-generate infographic after evaluation loads
  useEffect(() => {
    if (evaluation && !infographic && !isGenerating && availableProviders.length > 0) {
      generateInfographic();
    }
  }, [evaluation, availableProviders]);

  async function fetchProviders(): Promise<ProvidersResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/evaluations/infographic/providers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        return { success: false, providers: ['gemini'], default: 'gemini' };
      }
      
      return await response.json();
    } catch {
      return { success: false, providers: ['gemini'], default: 'gemini' };
    }
  }

  async function generateInfographic(regenerate = false) {
    if (!evaluationId) return;
    
    try {
      setIsGenerating(true);
      setError(null);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/evaluations/${evaluationId}/infographic`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({ provider: selectedProvider, regenerate }),
        }
      );
      
      const data: InfographicResponse = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to generate infographic');
      }
      
      setInfographic(data);
    } catch (err) {
      console.error("Failed to generate infographic:", err);
      setError(err instanceof Error ? err.message : "Failed to generate infographic");
    } finally {
      setIsGenerating(false);
    }
  }

  function handleRegenerate() {
    setInfographic(null);
    generateInfographic(true); // Force regeneration, skip cache
  }

  function handleProviderChange(provider: ImageGenerationProvider) {
    setSelectedProvider(provider);
    setShowProviderSettings(false);
    setInfographic(null);
    // Provider change will use cache if available via useEffect
  }

  function handleDownload() {
    if (!infographic) return;
    
    const imageData = infographic.image_base64 || infographic.image_url;
    if (!imageData) return;
    
    const link = document.createElement('a');
    
    if (infographic.image_base64) {
      link.href = `data:image/png;base64,${infographic.image_base64}`;
    } else if (infographic.image_url) {
      link.href = infographic.image_url;
    }
    
    link.download = `${evaluation?.vendor_name || 'vendor'}-infographic.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleExit() {
    navigate(-1);
  }

  // Loading evaluation state
  if (isLoadingEvaluation) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !infographic) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Failed to Generate Infographic</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleExit}
              className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={handleRegenerate}
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const imageSource = infographic?.image_base64 
    ? `data:image/png;base64,${infographic.image_base64}`
    : infographic?.image_url;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={handleExit}
                className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">Back</span>
              </button>
              <div className="h-6 w-px bg-slate-200 hidden sm:block" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                  {evaluation?.vendor_name} Infographic
                </h1>
                <p className="text-sm text-slate-500">
                  AI-generated visual summary
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Provider Settings */}
              <div className="relative">
                <button
                  onClick={() => setShowProviderSettings(!showProviderSettings)}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors text-sm"
                >
                  <Settings className="w-4 h-4" />
                  <span className="capitalize hidden sm:inline">{selectedProvider}</span>
                </button>
                {showProviderSettings && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                    <div className="p-2">
                      <p className="text-xs text-slate-500 px-3 py-1">Image Provider</p>
                      {availableProviders.map((provider) => (
                        <button
                          key={provider}
                          onClick={() => handleProviderChange(provider)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedProvider === provider
                              ? 'bg-amber-100 text-amber-900'
                              : 'hover:bg-slate-100 text-slate-700'
                          }`}
                        >
                          <span className="capitalize">{provider === 'gemini' ? 'Gemini Imagen' : 'DALL-E 3'}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Regenerate */}
              <button
                onClick={handleRegenerate}
                disabled={isGenerating}
                className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Regenerate</span>
              </button>
              {/* Download */}
              <button
                onClick={handleDownload}
                disabled={!infographic || isGenerating}
                className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium shadow-md disabled:opacity-50 text-sm"
              >
                <Download className="w-5 h-5" />
                <span className="hidden sm:inline">Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {isGenerating ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-24"
          >
            <div className="relative">
              <div className="w-24 h-24 border-4 border-amber-200 rounded-full animate-pulse" />
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-slate-600 mt-6 text-lg">Generating infographic with {selectedProvider === 'gemini' ? 'Gemini Imagen' : 'DALL-E 3'}...</p>
            <p className="text-slate-400 mt-2 text-sm">This may take 15-30 seconds</p>
          </motion.div>
        ) : imageSource ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            <img
              src={imageSource}
              alt={`${evaluation?.vendor_name} Infographic`}
              className="w-full h-auto"
            />
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-500">
                  Generated with <span className="font-medium capitalize">{infographic?.provider === 'gemini' ? 'Gemini Imagen' : 'DALL-E 3'}</span>
                </p>
                {infographic?.cached && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                    Cached
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">
                {evaluation?.vendor_category}
              </p>
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
